import moment from 'moment';

import { displayDateFormat_HH_MM , MARKET_DATA_DATE } from '../../lib/constants/date-time.constants';
import { CharacterEnum } from '../../lib/enums/character.enum';
import lang from '../../lib/language';
import { MarketHours, NonWorkingDay } from '../../lib/models/fundamental/types';
import { MarketState } from '../../lib/models/market-data/types';

import AfterHoursIcon from '../../assets/img/marketStates/after-hours.svg';
import ClosedMarketIcon from '../../assets/img/marketStates/closed-market.svg';
import OpenMarketIcon from '../../assets/img/marketStates/open-market.svg';
import PreMarketIcon from '../../assets/img/marketStates/pre-market.svg';

export const MARKET_STATE_COLORS = {
  preMarket: '#F6C98E',
  openMarket: '#1BFEBA',
  afterHours: '#D658DE',
  closedMarket: '#B4BFD2',
  inactive: '#3BB0FF33',
};

export const MARKET_STATE_ICON_BG_COLORS = {
  preMarket: '#F6C98E26',
  openMarket: '#1BFEBA26',
  afterHours: '#D658DE26',
  closedMarket: '#B4BFD226',
};

export const MARKET_STATE_TEXT_COLORS = {
  active: '#F2F4F7',
  inactive: '#B4BFD2',
};

export interface MarketStateConfig {
  key: string;
  verticalLineColor: string;
  title: string;
  iconBackgroundColor?: string;
  icon: string;
  iconAlt: string;
  timeText: string;
  description?: string;
  textColor?: string;
}

export const getMarketStates = (): MarketStateConfig[] => [
  {
    key: 'pre-market',
    verticalLineColor: MARKET_STATE_COLORS.preMarket,
    title: lang.commonMarketStatePreMarket(),
    iconBackgroundColor: MARKET_STATE_ICON_BG_COLORS.preMarket,
    icon: PreMarketIcon,
    iconAlt: 'Pre Market',
    timeText: '',
    description: lang.commonPreMarketHoursModalDescription(),
    textColor: MARKET_STATE_TEXT_COLORS.active,
  },
  {
    key: 'open-market',
    verticalLineColor: MARKET_STATE_COLORS.openMarket,
    title: lang.commonMarketStateOpen(),
    iconBackgroundColor: MARKET_STATE_ICON_BG_COLORS.openMarket,
    icon: OpenMarketIcon,
    iconAlt: 'Open Market',
    timeText: '',
    description: lang.commonOpenMarketHoursModalDescription(),
    textColor: MARKET_STATE_TEXT_COLORS.active,
  },
  {
    key: 'after-hours',
    verticalLineColor: MARKET_STATE_COLORS.afterHours,
    title: lang.commonMarketStateAfterHours(),
    iconBackgroundColor: MARKET_STATE_ICON_BG_COLORS.afterHours,
    icon: AfterHoursIcon,
    iconAlt: 'After Hours',
    timeText: '',
    description: lang.commonAfterMarketHoursModalDescription(),
    textColor: MARKET_STATE_TEXT_COLORS.active,
  },
  {
    key: 'closed-market',
    verticalLineColor: MARKET_STATE_COLORS.closedMarket,
    title: lang.commonMarketStateClosed(),
    iconBackgroundColor: MARKET_STATE_ICON_BG_COLORS.closedMarket,
    icon: ClosedMarketIcon,
    iconAlt: 'Closed Market',
    timeText: '',
    description: lang.commonClosedMarketHoursModalDescription(),
    textColor: MARKET_STATE_TEXT_COLORS.active,
  },
];

export const getActiveStateKey = (currentMarketState: MarketState | null): string | null => {
  switch (currentMarketState) {
    case MarketState.PreMarket:
      return 'pre-market';
    case MarketState.RegularHours:
      return 'open-market';
    case MarketState.AfterMarket:
      return 'after-hours';
    case MarketState.MarketClosed:
      return 'closed-market';
    default:
      return null;
  }
};

const isHoliday = (date: moment.Moment, nonWorkingDays: NonWorkingDay[]): boolean => {
  const dateString = date.format(MARKET_DATA_DATE);
  return nonWorkingDays.some(day => day.date === dateString && !day.halfDay);
};

// Returns the trading day on which a session still lies ahead (today if session hasn't ended yet,
// otherwise the next trading day), along with whether that day is a half-day.
const getNextSessionTradingDay = (
  todaySessionEndTime: string,
  timeZone: string,
  nonWorkingDays: NonWorkingDay[],
): { date: moment.Moment; isHalfDay: boolean } => {
  const now = moment.tz(timeZone);
  const sessionEndToday = moment.tz(todaySessionEndTime, displayDateFormat_HH_MM, timeZone);

  const isTodayWeekend = now.day() === 0 || now.day() === 6;
  const isTodayHoliday = isHoliday(now, nonWorkingDays);
  const isTodayTradingDay = !isTodayWeekend && !isTodayHoliday;

  if (isTodayTradingDay && now.isBefore(sessionEndToday)) {
    const todayDateString = now.format(MARKET_DATA_DATE);
    return {
      date: now.clone().startOf('day'),
      isHalfDay: nonWorkingDays.some(d => d.date === todayDateString && d.halfDay),
    };
  }

  const nextDay = getNextTradingDay(now, '00:00', nonWorkingDays);
  const nextDayDateString = nextDay.format(MARKET_DATA_DATE);
  return {
    date: nextDay.clone().startOf('day'),
    isHalfDay: nonWorkingDays.some(d => d.date === nextDayDateString && d.halfDay),
  };
};

const setTime = (date: moment.Moment, time: string): moment.Moment => {
  const [hours, minutes] = time.split(CharacterEnum.Colon);
  return date.clone().set({ hour: parseInt(hours, 10), minute: parseInt(minutes, 10), second: 0 });
};

const formatSessionTime = (startMoment: moment.Moment, endMoment: moment.Moment): string => {
  const dayName = startMoment.local().format('dddd');
  const startLocal = startMoment.local().format(displayDateFormat_HH_MM);
  const endLocal = endMoment.local().format(displayDateFormat_HH_MM);
  return `${dayName} ${startLocal} - ${endLocal}`;
};

const getNextTradingDay = (
  baseDate: moment.Moment,
  openTime: string,
  nonWorkingDays: NonWorkingDay[],
): moment.Moment => {
  const nextDay = baseDate.clone().add(1, 'day');

  while (nextDay.day() === 0 || nextDay.day() === 6 || isHoliday(nextDay, nonWorkingDays)) {
    nextDay.add(1, 'day');
  }

  const [hours, minutes] = openTime.split(CharacterEnum.Colon);
  nextDay.set({ hour: parseInt(hours, 10), minute: parseInt(minutes, 10), second: 0 });

  return nextDay;
};

const getPreviousTradingDay = (
  baseDate: moment.Moment,
  nonWorkingDays: NonWorkingDay[],
): moment.Moment => {
  const prevDay = baseDate.clone().subtract(1, 'day');

  while (prevDay.day() === 0 || prevDay.day() === 6 || isHoliday(prevDay, nonWorkingDays)) {
    prevDay.subtract(1, 'day');
  }

  return prevDay;
};

type CalculateTimeTextParams = {
  stateKey: string;
  marketHours: MarketHours;
  timeZone: string;
  isHalfDay: boolean;
  nonWorkingDays: NonWorkingDay[];
};

const calculateStateTimeText = (params: CalculateTimeTextParams): string => {
  const { stateKey, marketHours, timeZone, isHalfDay, nonWorkingDays } = params;

  switch (stateKey) {
    case 'pre-market': {
      const { date } = getNextSessionTradingDay(marketHours.open, timeZone, nonWorkingDays);
      return formatSessionTime(setTime(date, marketHours.preMarketStart), setTime(date, marketHours.open));
    }
    case 'open-market': {
      const todayCloseTime = isHalfDay ? marketHours.closeForHalfDay : marketHours.close;
      const { date, isHalfDay: dayIsHalfDay } = getNextSessionTradingDay(todayCloseTime, timeZone, nonWorkingDays);
      const closeTime = dayIsHalfDay ? marketHours.closeForHalfDay : marketHours.close;
      return formatSessionTime(setTime(date, marketHours.open), setTime(date, closeTime));
    }
    case 'after-hours': {
      const todayAfterEndTime = isHalfDay ? marketHours.afterMarketEndForHalfDay : marketHours.afterMarketEnd;
      const { date, isHalfDay: dayIsHalfDay } = getNextSessionTradingDay(todayAfterEndTime, timeZone, nonWorkingDays);
      const closeTime = dayIsHalfDay ? marketHours.closeForHalfDay : marketHours.close;
      const afterEndTime = dayIsHalfDay ? marketHours.afterMarketEndForHalfDay : marketHours.afterMarketEnd;
      return formatSessionTime(setTime(date, closeTime), setTime(date, afterEndTime));
    }
    case 'closed-market': {
      const now = moment.tz(timeZone);
      const nextDayOpenTime = marketHours.preMarketStart;

      // Early morning: we're after midnight but before pre-market/open, meaning the closed
      // period started on the previous trading day — not today.
      const earlyMorningCutoff = moment.tz(nextDayOpenTime, displayDateFormat_HH_MM, timeZone);
      const isEarlyMorning = now.isBefore(earlyMorningCutoff);

      let closedStart: moment.Moment;

      if (isEarlyMorning) {
        const prevDay = getPreviousTradingDay(now, nonWorkingDays);
        const prevDayDateString = prevDay.format(MARKET_DATA_DATE);
        const prevDayIsHalfDay = nonWorkingDays.some(d => d.date === prevDayDateString && d.halfDay);
        const closedStartTime = prevDayIsHalfDay
          ? marketHours.afterMarketEndForHalfDay
          : marketHours.afterMarketEnd;
        closedStart = setTime(prevDay, closedStartTime);
      } else {
        const isTodayWeekend = now.day() === 0 || now.day() === 6;
        const isTodayHoliday = isHoliday(now, nonWorkingDays);

        if (isTodayWeekend || isTodayHoliday) {
          const prevDay = getPreviousTradingDay(now, nonWorkingDays);
          const prevDayDateString = prevDay.format(MARKET_DATA_DATE);
          const prevDayIsHalfDay = nonWorkingDays.some(d => d.date === prevDayDateString && d.halfDay);
          const closedStartTime = prevDayIsHalfDay
            ? marketHours.afterMarketEndForHalfDay
            : marketHours.afterMarketEnd;
          closedStart = setTime(prevDay, closedStartTime);
        } else {
          const closedStartTime = isHalfDay
            ? marketHours.afterMarketEndForHalfDay
            : marketHours.afterMarketEnd;
          closedStart = moment.tz(closedStartTime, displayDateFormat_HH_MM, timeZone);
        }
      }

      const nextDayOpen = getNextTradingDay(closedStart, nextDayOpenTime, nonWorkingDays);

      const closedStartLocal = closedStart.local().format(displayDateFormat_HH_MM);
      const nextDayOpenLocal = nextDayOpen.local().format(displayDateFormat_HH_MM);
      const closedDayName = closedStart.local().format('dddd');
      const nextDayName = nextDayOpen.local().format('dddd');

      return `${closedDayName} ${closedStartLocal} - ${nextDayName} ${nextDayOpenLocal}`;
    }
    default:
      return '';
  }
};

const applyStateStyling = (
  state: MarketStateConfig,
  isActiveState: boolean,
): MarketStateConfig => {
  return {
    ...state,
    description: isActiveState ? state.description : undefined,
    verticalLineColor: isActiveState ? state.verticalLineColor : MARKET_STATE_COLORS.inactive,
    iconBackgroundColor: isActiveState ? state.iconBackgroundColor : undefined,
    textColor: isActiveState ? MARKET_STATE_TEXT_COLORS.active : MARKET_STATE_TEXT_COLORS.inactive,
  };
};

const reorderStatesByActive = (
  states: MarketStateConfig[],
  activeStateKey: string | null,
): MarketStateConfig[] => {
  if (!activeStateKey) {
    return states;
  }

  const activeIndex = states.findIndex(state => state.key === activeStateKey);
  if (activeIndex === -1) {
    return states;
  }

  return [...states.slice(activeIndex), ...states.slice(0, activeIndex)];
};

export const getMarketStateWithTimes = (
  marketHours: MarketHours,
  marketState: MarketState | null,
  nonWorkingDays: NonWorkingDay[] = [],
): MarketStateConfig[] => {
  const baseStates = getMarketStates();
  const timeZone = marketHours.timeZone;
  const activeStateKey = getActiveStateKey(marketState);

  const todayString = moment.tz(timeZone).format(MARKET_DATA_DATE);
  const isHalfDay = nonWorkingDays.some(day => day.date === todayString && day.halfDay);

  const statesWithTimes = baseStates.map(state => {
    const timeText = calculateStateTimeText({
      stateKey: state.key,
      marketHours,
      timeZone,
      isHalfDay,
      nonWorkingDays,
    });

    return applyStateStyling({ ...state, timeText }, state.key === activeStateKey);
  });

  return reorderStatesByActive(statesWithTimes, activeStateKey);
};
